import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Home,
  Package,
  ShoppingCart,
  Settings,
  LogOut,
  Menu,
  X,
  User,
  Bell,
  Key,
  CreditCard
} from 'lucide-react';

const Layout = () => {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/', icon: Home },
    { name: 'Products', href: '/products', icon: Package },
    { name: 'Manage Orders', href: '/orders', icon: ShoppingCart },
    { name: 'Manage Keys', href: '/keys', icon: Key },
    { name: 'Transactions', href: '/transactions', icon: CreditCard },
  ];

  if (isAdmin) {
    navigation.push({ name: 'Admin', href: '/admin', icon: Settings });
  }

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActivePath = (path) => {
    if (path === '/') {
      return location.pathname === '/' || location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen" style={{backgroundColor: '#0a0a0a'}}>
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-opacity-75" style={{backgroundColor: '#0a0a0a'}} onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-56 flex-col shadow-xl border-r" style={{backgroundColor: '#1a1a1a', borderColor: '#4a4a4a'}}>
          <div className="flex h-16 items-center justify-between px-4">
            <div className="flex items-center">
              <span className="text-2xl font-bold text-gray-100">Reign</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-gray-300"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          {/* User Profile Box - Mobile */}
          <div className="mx-4 mb-4 p-3 rounded-lg flex items-center" style={{backgroundColor: '#0a0a0a'}}>
            <User className="h-10 w-10 rounded-full p-2 text-gray-300 flex-shrink-0" style={{backgroundColor: '#2a2a2a'}} />
            <div className="ml-3 flex-1 min-w-0">
              <div className="text-xs truncate" style={{color: '#3b198e'}}>{user?.discord_username || user?.username}</div>
              <div className="text-xs text-gray-400 capitalize truncate">{user?.role}</div>
            </div>
          </div>

          <nav className="flex-1 space-y-2 px-2 py-4">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className="group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer text-gray-300 hover:text-white"
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2a2a2a'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
                >
                  <Icon className="mr-3 h-5 w-5 flex-shrink-0 pointer-events-none" style={isActivePath(item.href) ? {color: '#3b198e', stroke: '#3b198e'} : {}} />
                  <span className="pointer-events-none" style={isActivePath(item.href) ? {color: '#3b198e'} : {}}>{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* Bottom separator and buttons - Mobile */}
          <div className="border-t mx-4 mt-4" style={{borderColor: '#4a4a4a'}}></div>
          <div className="px-2 py-4 space-y-2">
            <Link
              to="/account"
              onClick={() => setSidebarOpen(false)}
              className="group flex items-center px-2 py-2 text-sm font-medium rounded-md text-gray-300 hover:text-white transition-colors cursor-pointer"
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2a2a2a'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
            >
              <User className="mr-3 h-5 w-5 flex-shrink-0 pointer-events-none" />
              <span className="pointer-events-none">Account</span>
            </Link>
            <button
              onClick={() => { setSidebarOpen(false); handleLogout(); }}
              className="w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md text-gray-300 hover:text-white transition-colors cursor-pointer"
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2a2a2a'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
            >
              <LogOut className="mr-3 h-5 w-5 flex-shrink-0 pointer-events-none" />
              <span className="pointer-events-none">Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-56 lg:flex-col">
        <div className="flex flex-col flex-grow border-r pt-5 pb-4 overflow-y-auto" style={{backgroundColor: '#1a1a1a', borderColor: '#4a4a4a'}}>
          <div className="flex items-center justify-center px-4 pb-5">
            <span className="text-2xl font-bold text-gray-100">Reign</span>
          </div>
          <div className="border-t mx-4 mb-5" style={{borderColor: '#4a4a4a'}}></div>
          
          {/* User Profile Box */}
          <div className="mx-4 mb-4 p-3 rounded-lg flex items-center" style={{backgroundColor: '#0a0a0a'}}>
            <User className="h-10 w-10 rounded-full p-2 text-gray-300 flex-shrink-0" style={{backgroundColor: '#2a2a2a'}} />
            <div className="ml-3 flex-1 min-w-0">
              <div className="text-xs truncate" style={{color: '#3b198e'}}>{user?.discord_username || user?.username}</div>
              <div className="text-xs text-gray-400 capitalize truncate">{user?.role}</div>
            </div>
          </div>

          <nav className="flex-1 flex flex-col divide-y overflow-y-auto" style={{borderColor: '#4a4a4a'}}>
            <div className="px-2 space-y-2">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer text-gray-300 hover:text-white"
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2a2a2a'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
                  >
                    <Icon className="mr-3 h-5 w-5 flex-shrink-0 pointer-events-none" style={isActivePath(item.href) ? {color: '#3b198e', stroke: '#3b198e'} : {}} />
                    <span className="pointer-events-none" style={isActivePath(item.href) ? {color: '#3b198e'} : {}}>{item.name}</span>
                  </Link>
                );
              })}
            </div>
          </nav>

          {/* Bottom separator and buttons */}
          <div className="border-t mx-4 mt-4" style={{borderColor: '#4a4a4a'}}></div>
          <div className="px-2 py-4 space-y-2">
            <Link
              to="/account"
              className="group flex items-center px-2 py-2 text-sm font-medium rounded-md text-gray-300 hover:text-white transition-colors cursor-pointer"
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2a2a2a'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
            >
              <User className="mr-3 h-5 w-5 flex-shrink-0 pointer-events-none" />
              <span className="pointer-events-none">Account</span>
            </Link>
            <button
              onClick={handleLogout}
              className="w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md text-gray-300 hover:text-white transition-colors cursor-pointer"
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2a2a2a'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
            >
              <LogOut className="mr-3 h-5 w-5 flex-shrink-0 pointer-events-none" />
              <span className="pointer-events-none">Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-56 flex flex-col flex-1">
        {/* Top navigation */}
        <div className="sticky top-0 z-10 flex-shrink-0 flex h-16 shadow border-b" style={{backgroundColor: '#1a1a1a', borderColor: '#4a4a4a'}}>
          <button
            onClick={() => setSidebarOpen(true)}
            className="px-4 border-r text-gray-400 focus:outline-none focus:ring-2 focus:ring-inset lg:hidden"
            style={{borderColor: '#4a4a4a', '--tw-ring-color': '#3b198e'}}
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex-1 px-4 flex justify-between">
            <div className="flex-1 flex">
              {/* Search could go here */}
            </div>
            <div className="ml-4 flex items-center md:ml-6">
              {/* Notifications */}
              <button className="p-1 rounded-full text-gray-400 hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2" style={{backgroundColor: '#1a1a1a', '--tw-ring-color': '#3b198e'}}>
                <Bell className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1">
          <div className="mx-auto px-7">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
